// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Typography, Button, RadioGroup, Radio, FormControlLabel } from '@material-ui/core';
import { toast } from 'react-toastify';
import Select from 'react-select';

// Internal Dependencies
import AreaMap from '../areaMap/areaMap';
import Constants from '../../common/constants';
import { IsEmpty } from '../../common/checks';
import { Sleep } from '../../common/util';
import apiService from '../../services.js/apiService';

const GeographicTarget = ({ adId, activeStep, onActiveStepChanged }) => {
  const radioLabels = [
    Constants.GeographicTargetType.NA,
    Constants.GeographicTargetType.Area,
    Constants.GeographicTargetType.Mall,
    Constants.GeographicTargetType.AreaManagement,
    Constants.GeographicTargetType.Custom,
  ];

  const [geographicTargetType, setGeographicTargetType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [area, setArea] = useState(null);
  const [areaOptions, setAreaOptions] = useState([]);
  const [page, setPage] = useState(null);
  const [pageOptions, setPageOptions] = useState([]);
  const [coords, setCoords] = useState([]);

  const typeChangeHandler = async (type) => {
    try {
      // don't show loading for type "custom", because it has it's own loading text
      if (type !== Constants.GeographicTargetType.Custom) {
        setLoading(true);
      }

      setGeographicTargetType(type);

      if (type === Constants.GeographicTargetType.Area) {
        const areas = await apiService.getAreas();
        const formattedAreas = _.map(areas, (areaData) => ({ id: areaData.id, label: areaData.name, value: areaData.name }));

        setAreaOptions(formattedAreas);
      } else if ([Constants.GeographicTargetType.Mall, Constants.GeographicTargetType.AreaManagement].includes(type)) {
        const pages = await apiService.getPagesWithBoundsForMallOrAreaManagement(type); // "type" is the pageType
        const formattedPages = _.map(pages, (pageData) => ({ id: pageData.pageId, label: pageData.name, value: pageData.name }));

        setPageOptions(formattedPages);
        setPage(null);
      }
    } catch (error) {
      toast.error(error.message);
      console.log('Error in typeChangeHandler: ', error);
    } finally {
      await Sleep(100); // for the UI
      setLoading(false);
    }
  };

  const getGeographicTargetDataHandler = async () => {
    try {
      setLoading(true);
      const mapBound = await apiService.getMapBoundAd(adId);
      let areaData = null;
      let pageData = null;
      let type = null;

      if (!IsEmpty(mapBound.targetAreaId)) {
        const record = await apiService.getAreaById(mapBound.targetAreaId);
        areaData = { id: record.id, label: record.name, value: record.name };
        type = Constants.GeographicTargetType.Area;
      } else if (!IsEmpty(mapBound.targetPageId)) {
        const record = await apiService.getPageById(mapBound.targetPageId);
        pageData = { id: record.id, label: record.name, value: record.name };
        type = record.type; // "Mall" or "Area Management"
      } else if (!_.isEmpty(mapBound.coords)) {
        type = Constants.GeographicTargetType.Custom;
      } else {
        type = Constants.GeographicTargetType.NA;
      }

      // update coords data first to ensure the areaMap has sufficient data for display, before updating "setGeographicTargetType"
      setCoords(mapBound.coords || []);

      // fetch the type data
      await typeChangeHandler(type);

      setArea(areaData || null);
      setPage(pageData || null);
      setData(mapBound);
      setGeographicTargetType(type);
    } catch (error) {
      toast.error('Error while fetching data for geographic target');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        adId:         !IsEmpty(adId) ? Number(adId) : null,
        targetAreaId: null,
        targetPageId: null,
        coords:       null,
      };

      if (geographicTargetType === Constants.GeographicTargetType.NA) {
        payload.adId = null;
      } else if (geographicTargetType === Constants.GeographicTargetType.Area) {
        if (IsEmpty(area)) {
          return toast.error('Please select an area');
        }

        payload.targetAreaId = area.id;
      } else if ([Constants.GeographicTargetType.Mall, Constants.GeographicTargetType.AreaManagement].includes(geographicTargetType)) {
        if (IsEmpty(page)) {
          return toast.error('Please select a page');
        }

        payload.targetPageId = page.id;
      } else if (geographicTargetType === Constants.GeographicTargetType.Custom) {
        payload.targetAreaId = null;
        payload.targetPageId = null;
        payload.coords = coords;
      }

      if (IsEmpty(data)) { // CREATE
        // we don't need to create a record for type "Not Applicable"
        if (geographicTargetType === Constants.GeographicTargetType.NA) {
          // fetch latest data
          await getGeographicTargetDataHandler();
          return toast.success('Geographic target successfully saved');
        }

        await apiService.createMapBoundAd(adId, payload);
        toast.success('Geographic target successfully saved');
      } else { // UPDATE
        // If type is "Not Applicable", then remove the ad record
        if (geographicTargetType === Constants.GeographicTargetType.NA) {
          await apiService.deleteMapBoundAd(adId);

          // fetch latest data
          await getGeographicTargetDataHandler();
          return toast.success('Geographic target successfully saved');
        }

        payload.id = data.id;
        await apiService.updateMapBoundAd(adId, payload);
        toast.success('Geographic target successfully saved');
      }

      // fetch latest data
      await getGeographicTargetDataHandler();
    } catch (error) {
      toast.error(error.message);
      console.log('Error in onSaveHandler: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGeographicTargetDataHandler();
  }, []);

  return (
    <div>
      <h5>Geographic Target</h5>
      <RadioGroup
        className="mt-2"
        row
        aria-label="type"
        name="type"
        value={geographicTargetType}
        onChange={(event) => typeChangeHandler(event.target.value)}
      >
        {_.map(radioLabels, (label) => (
          <FormControlLabel
            key={label}
            value={label}
            control={<Radio color="primary" />}
            label={label === Constants.GeographicTargetType.NA ? 'N.A' : label}
          />
        ))}
      </RadioGroup>

      { loading ? <Typography variant="subtitle1" color="inherit">Loading...</Typography> : (
        <>
          { geographicTargetType === Constants.GeographicTargetType.Area && (
            <div className="mt-2">
              <Typography className="mb-1" variant="subtitle1" color="inherit">Select Area as the geography target</Typography>
              <div style={{ width: '250px' }}>
                <Select
                  value={area}
                  onChange={(option) => setArea(option)}
                  options={areaOptions}
                  isClearable
                />
              </div>
            </div>
          ) }

          { [Constants.GeographicTargetType.Mall, Constants.GeographicTargetType.AreaManagement].includes(geographicTargetType) && (
            <div className="mt-2">
              <Typography className="mb-1" variant="subtitle1" color="inherit">Select Page for the geography target</Typography>
              <div style={{ width: '250px' }}>
                <Select
                  value={page}
                  onChange={(option) => setPage(option)}
                  options={pageOptions}
                  isClearable
                />
              </div>
            </div>
          ) }
        </>
      ) }

      { ((geographicTargetType === Constants.GeographicTargetType.Custom) && !loading) && (
        <AreaMap
          style={{ padding: 0 }}
          buttonContainerStyles={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
          buttonSubContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
          Heading={() => (
            <Typography variant="subtitle1" color="inherit">
              Click region in map as custom geographic target
            </Typography>
          )}
          setMapCoor={(c) => setCoords(c)}
          mapCoordinates={coords}
        />
      ) }

      <div className="d-flex justify-content-between align-items-center w-100">
        <Button onClick={onSaveHandler} className="mt-4" variant="contained" color="primary">Save</Button>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginTop: '8px' }}>
          <Button onClick={() => onActiveStepChanged(activeStep - 1)} variant="contained" color="default">Back</Button>
          <Button onClick={() => onActiveStepChanged(activeStep + 1)} variant="contained" color="default">Next</Button>
        </div>
      </div>
    </div>
  );
};

export default GeographicTarget;

GeographicTarget.propTypes = {
  adId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  activeStep:          PropTypes.number.isRequired,
  onActiveStepChanged: PropTypes.func.isRequired,
};
